import * as React from 'react';
import { containerFactory } from 'app/infrastructure/redux/container-factory';
import { actionCreators } from 'app/area/case/draft/actions';
import { STATE } from 'app/constants';
import { ComplexValue, SingleValueComponent } from 'ui/common/types';
import { MultiEmails } from 'app/area/case/components/multi-emails';
import { codeItemToStoreValue } from 'app/infrastructure/data/codes';
import { CodetableWireFormat } from 'app/infrastructure/ui/form/types-codetable';
import { isValidListOfEmails } from 'app/infrastructure/validation/email';
import { EmailRecipientsSelector } from 'app/area/case/components/share-by-email/email-recipients-selector';
import styles from './multi-select-recipients.scss';
import { ShareLinkContext } from 'app/area/case/components/share-by-email';

interface MultiSelectRecipientsProps extends SingleValueComponent<string> {
  recipients: string[];
  getUsers: (draftId: number, query?: string, domainNoUsers?: number) => CodetableWireFormat[];
  entityId: number;
}

interface MultiSelectRecipientsTranslations {
  textDone?: string;
  textPlaceholderSearch?: string;
  textAddItem?: string;
  textCodeSelectorTitle?: string;
  textExternalRecipients?: string;
  textPlaceholderEmail?: string;
}

export interface MultiSelectRecipientsState {
  options?: ComplexValue<string>[];
  recipients: string[];
}

export type MultiSelectRecipientsComponentProps = MultiSelectRecipientsProps & MultiSelectRecipientsTranslations;

export class MultiSelectRecipientsComponent extends React.PureComponent<MultiSelectRecipientsComponentProps, MultiSelectRecipientsState> {
  public static statePath: string = STATE.app;

  public constructor(props: MultiSelectRecipientsProps) {
    super(props);
    this.state = {
      recipients: (props.value?.split(';') || [])
    };
  }

  private loadRecipients = (domainNoUsers: number) => async ({query} : {query: string}) => {
    const users = await this.props.getUsers(this.props.entityId, query, domainNoUsers);
    this.setState({ options: users?.map(codeItemToStoreValue) });
  };

  private handleChangeExternalRecipients = (emails: string[]) => {
    this.setState({
      recipients: emails 
    });
    this.props.onChange(emails?.join(';'));
  }; 

  private handleChangeRecipients = (values: ComplexValue<string>[]) => {
    const emails = values?.map(v => v.value).join(';');
    if( isValidListOfEmails(emails) ) {
      const mergedList = this.state.recipients.concat(values.map(v => v.value));
      const uniqueEmails = mergedList?.filter((v,i) => mergedList.indexOf(v) === i);
      this.handleChangeExternalRecipients(uniqueEmails);
    }
  };

  private excludeSelectedOptions = (options: ComplexValue<string>[]): ComplexValue<string>[] => {

    return options?.filter(e => this.state.recipients?.indexOf(e.value) === -1);      
  };

  public render() {
    const {
      componentId,
      className,
      textDone,
      textPlaceholderSearch,
      textAddItem,
      textCodeSelectorTitle,
      textExternalRecipients,
      textPlaceholderEmail
    } = this.props;

    const {
      options,
      recipients
    } = this.state;

    return (
      <div id={componentId} className={`${className}`}>
        <div className={`${styles.recipientsWrapper} form-group email-recipients`}>
        <MultiEmails
          componentId={'external-recipients'}
          className={'external-recipients-with-extra-label'}
          emails={recipients}
          onChange={this.handleChangeExternalRecipients}
          extraLabel={textExternalRecipients}
          placeholder={textPlaceholderEmail}
        />
        <ShareLinkContext.Consumer>
          {value => 
            <EmailRecipientsSelector
              {...this.props}
              options={this.excludeSelectedOptions?.(options)}
              textDone={textDone}
              textAddItem={textAddItem}
              textCodeSelectorTitle={textCodeSelectorTitle}
              placeholderSearch={textPlaceholderSearch}
              onLoadOptions={this.loadRecipients(value.domainNoUsers)}
              onClose={this.handleChangeRecipients}
              searchRequired={value.searchRequired}
            />}
        </ShareLinkContext.Consumer>
        </div>
      </div>
    );
  }
}

const c = containerFactory((MultiSelectRecipientsComponent), actionCreators, undefined);

export { c as MultiSelectRecipients };
